import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faPhone, faBalanceScale, faShoppingCart, faRupeeSign,
    faMoneyBill, faHistory, faExclamationTriangle, faFilter, faChevronDown,
    faChevronUp, faChevronLeft, faChevronRight, faSearch, faSpinner, faTimes,
    faSave, faPrint, faReceipt, faUsers, faEye, faCreditCard,
    faWallet, faUniversity, faCheckCircle, faExclamationCircle, faTag, faCalendarAlt,
    faList, faFileInvoice, faCheckSquare, faSquare, faPercent,
    faLandmark, faBarcode, faBuilding, faAddressCard, faIdCard, faCopy,
    faMoneyBillWave, faClock, faUserPlus, faBank, faInfoCircle, faBox,
    faSort, faSortUp, faSortDown, faCalendarWeek, faCalendarDay, faMobileAlt,
    faPlusCircle, faRedoAlt, faMinusCircle, faEdit, faTrash
} from '@fortawesome/free-solid-svg-icons';
import "../styles/POCustomer.css";

// ==============================================
// Debounce Function
// ==============================================
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// ==============================================
// Toast Notification Component
// ==============================================
const CaToastNotification = ({ toast, setToast, translations }) => {
    if (!toast.show) return null;
    return (
        <div className={`ca-toast-notification ${toast.type}`}>
            <div className="ca-toast-content">
                <div className="ca-toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationCircle} />
                </div>
                <div className="ca-toast-text">
                    <div className="ca-toast-message">{toast.message}</div>
                </div>
                <button className="ca-toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Sortable Table Header Component
// ==============================================
const CaSortableHeader = ({ column, label, sortConfig, onSort, translations }) => {
    const isActive = sortConfig.key === column;
    return (
        <th onClick={() => onSort(column)} className="ca-sortable-header">
            {label}
            <span className="ca-sort-indicator">
                {isActive ? (
                    sortConfig.direction === 'asc' ?
                        <FontAwesomeIcon icon={faSortUp} /> :
                        <FontAwesomeIcon icon={faSortDown} />
                ) : (
                    <FontAwesomeIcon icon={faSort} />
                )}
            </span>
        </th>
    );
};

// ==============================================
// Edit Transaction Modal Component
// ==============================================
const EditTransactionModal = ({ show, transaction, customer, onClose, onSave, showToast, currentShopId }) => {
    const [amount, setAmount] = useState('');
    const [transactionDate, setTransactionDate] = useState('');
    const [description, setDescription] = useState('');
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && transaction) {
            setAmount(transaction.amount?.toString() || '');
            setTransactionDate(transaction.transaction_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
            setDescription(transaction.description || '');
            setReferenceNo(transaction.reference_number || '');
            setNotes(transaction.notes || '');
            setErrors({});
        }
    }, [show, transaction]);

    const validateForm = () => {
        const newErrors = {};
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = 'Please enter a valid amount';
        }
        if (!transactionDate) {
            newErrors.transaction_date = 'Transaction date is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            
            const payload = {
                action: 'update_po_transaction',
                transaction_id: transaction.id,
                customer_id: customer?.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                transaction_date: transactionDate,
                description: description,
                reference_number: referenceNo,
                notes: notes,
                username: username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 'Transaction updated successfully');
                if (onSave) {
                    await onSave();
                }
                onClose();
            } else {
                showToast('error', data.message || 'Failed to update transaction');
            }
        } catch (error) {
            console.error('Update error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-edit-transaction-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faEdit} /> Edit Transaction</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {transaction && customer && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">
                                <FontAwesomeIcon icon={faUser} />
                                {customer.customer_name}
                            </div>
                            <div className="ca-transaction-type-info">
                                <span className={`ca-transaction-type-badge ${transaction.transaction_type}`}>
                                    {transaction.transaction_type === 'sale' ? 'Sale' : 
                                     transaction.transaction_type === 'payment' ? 'Payment' :
                                     transaction.transaction_type === 'manual_balance' ? 'Manual Balance' : 'Adjustment'}
                                </span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">Amount <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                </div>

                                <div className={`ca-form-group ${errors.transaction_date ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={transactionDate}
                                            onChange={(e) => setTransactionDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">Transaction Date <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.transaction_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.transaction_date}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">Reference Number</label>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">Description</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">Notes</label>
                                </div>
                            </div>
                        </div>

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> Save Changes</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Delete Confirmation Modal Component
// ==============================================
const DeleteTransactionModal = ({ show, transaction, customer, onClose, onConfirm, showToast, currentShopId }) => {
    const [deleting, setDeleting] = useState(false);
    const { user } = useAuth();
    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    const handleConfirm = async () => {
        if (deleting) return;
        setDeleting(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            
            const payload = {
                action: 'delete_po_transaction',
                transaction_id: transaction.id,
                customer_id: customer?.customer_id,
                shop_id: shopId,
                username: username
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', 'Transaction deleted successfully');
                if (onConfirm) {
                    await onConfirm();
                }
                onClose();
            } else {
                showToast('error', data.message || 'Failed to delete transaction');
            }
        } catch (error) {
            console.error('Delete error:', error);
            showToast('error', 'Network error occurred');
        } finally {
            setDeleting(false);
        }
    };

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-delete-confirm-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faTrash} /> Delete Transaction</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    <div className="ca-delete-warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="ca-warning-icon" />
                        <p>Are you sure you want to delete this transaction?</p>
                        <p className="ca-warning-text">This action cannot be undone and will affect the customer's balance.</p>
                    </div>
                    
                    {transaction && (
                        <div className="ca-transaction-details">
                            <div className="ca-detail-row">
                                <span>Transaction Type:</span>
                                <strong>{transaction.transaction_type === 'sale' ? 'Sale' : 
                                         transaction.transaction_type === 'payment' ? 'Payment' :
                                         transaction.transaction_type === 'manual_balance' ? 'Manual Balance' : 'Adjustment'}</strong>
                            </div>
                            <div className="ca-detail-row">
                                <span>Amount:</span>
                                <strong className={transaction.transaction_type === 'sale' || transaction.transaction_type === 'manual_balance' ? 'ca-amount-debit' : 'ca-amount-credit'}>
                                    {transaction.transaction_type === 'sale' || transaction.transaction_type === 'manual_balance' ? `+ ` : `- `}
                                    Rs. {parseFloat(transaction.amount || 0).toFixed(2)}
                                </strong>
                            </div>
                            <div className="ca-detail-row">
                                <span>Date:</span>
                                <strong>{new Date(transaction.transaction_date).toLocaleDateString()}</strong>
                            </div>
                            {transaction.description && (
                                <div className="ca-detail-row">
                                    <span>Description:</span>
                                    <strong>{transaction.description}</strong>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="ca-modal-footer">
                    <button type="button" className="ca-btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className="ca-btn-danger" onClick={handleConfirm} disabled={deleting}>
                        {deleting ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Deleting...</>
                        ) : (
                            <><FontAwesomeIcon icon={faTrash} /> Delete Transaction</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Manual Balance Modal Component
// ==============================================
const CaManualBalanceModal = ({ show, customer, onClose, onSave, translations, showToast, currentShopId, onRefresh }) => {
    const [amount, setAmount] = useState('');
    const [balanceDate, setBalanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [description, setDescription] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && customer) {
            setAmount('');
            setBalanceDate(new Date().toISOString().split('T')[0]);
            setReferenceNo('');
            setDescription('');
            setNotes('');
            setErrors({});
        }
    }, [show, customer]);

    const validateForm = () => {
        const newErrors = {};

        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = translations?.validAmountRequired || 'Please enter a valid amount';
        }

        if (!balanceDate) {
            newErrors.balance_date = translations?.balanceDateRequired || 'Balance date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (saving) return;
        setSaving(true);

        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            const payload = {
                action: 'add_po_manual_balance',
                customer_id: customer.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                balance_date: balanceDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                description: description || 'Manual balance added for previous dues',
                idempotency_key: transactionId
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                if (showToast) {
                    showToast('success', data.message || translations?.manualBalanceSuccess || 'Manual balance added successfully');
                }
                if (onSave) {
                    await onSave(data.data);
                }
                if (onRefresh) {
                    onRefresh();
                }
                onClose();
            } else {
                if (showToast) {
                    showToast('error', data.message || translations?.manualBalanceFailed || 'Failed to add manual balance');
                } else {
                    alert(data.message || translations?.manualBalanceFailed || 'Failed to add manual balance');
                }
            }
        } catch (error) {
            console.error('Manual balance error:', error);
            if (showToast) {
                showToast('error', translations?.networkError || 'Network error occurred');
            } else {
                alert(translations?.networkError || 'Network error occurred');
            }
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-manual-balance-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faPlusCircle} /> {translations?.manualBalance || 'Add Manual Balance'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="ca-modal-body">
                    {customer && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">
                                <FontAwesomeIcon icon={faUser} />
                                {customer.customer_name}
                            </div>
                            {customer.phone && (
                                <div className="ca-customer-phone-large">
                                    <FontAwesomeIcon icon={faPhone} />
                                    {customer.phone}
                                </div>
                            )}
                            <div className="ca-current-balance">
                                <span>{translations?.currentBalance || 'Current Balance'}: </span>
                                <span>{formatCurrency(customer.current_balance || 0)}</span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faFileInvoice} /> {translations?.balanceDetails || 'Balance Details'}</h3>

                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faRupeeSign} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                    <div className="ca-help-text">
                                        {translations?.manualBalanceHelp || 'Enter the previous due amount to add to customer\'s account'}
                                    </div>
                                </div>

                                <div className={`ca-form-group ${errors.balance_date ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={balanceDate}
                                            onChange={(e) => setBalanceDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.balanceDate || 'Balance Date'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.balance_date && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.balance_date}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">{translations?.referenceNumber || 'Reference Number'}</label>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container">
                                    <FontAwesomeIcon icon={faFileInvoice} className="ca-input-icon" />
                                    <input
                                        type="text"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label className="ca-input-label">{translations?.description || 'Description'}</label>
                                </div>
                            </div>

                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes (e.g., Previous purchases, Old dues, etc.)'}</label>
                                </div>
                            </div>
                        </div>

                        {amount && parseFloat(amount) > 0 && customer && (
                            <div className="ca-balance-summary">
                                <h4>{translations?.balanceSummary || 'Balance Summary'}</h4>
                                <div className="ca-summary-row">
                                    <span>{translations?.currentBalance || 'Current Balance'}:</span>
                                    <span>{formatCurrency(customer.current_balance || 0)}</span>
                                </div>
                                <div className="ca-summary-row ca-success">
                                    <span>{translations?.amountToAdd || 'Amount to Add'}:</span>
                                    <span>+ {formatCurrency(amount)}</span>
                                </div>
                                <div className="ca-summary-row ca-border-top ca-bold">
                                    <span>{translations?.newBalance || 'New Balance After Addition'}:</span>
                                    <span>{formatCurrency((customer.current_balance || 0) + parseFloat(amount))}</span>
                                </div>
                            </div>
                        )}

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                {translations?.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.adding || 'Adding...'}</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> {translations?.addBalance || 'Add Balance'}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Payment Modal Component
// ==============================================
const POEnhancedPaymentModal = ({ show, customer, onClose, onSave, translations, showToast, currentShopId }) => {
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const { user } = useAuth();

    const shopId = currentShopId || localStorage.getItem('current_shop_id') || '';

    useEffect(() => {
        if (show && customer) {
            setAmount('');
            setReferenceNo('');
            setNotes('');
            setBankName('');
            setAccountNumber('');
            setTransactionId('');
            setPaymentMethod('cash');
            setErrors({});
        }
    }, [show, customer]);

    const validateForm = () => {
        const newErrors = {};
        if (!amount || parseFloat(amount) <= 0) {
            newErrors.amount = translations?.invalidAmount || 'Please enter a valid amount';
        } else if (parseFloat(amount) > (customer?.current_balance || 0) && (customer?.current_balance || 0) > 0) {
            newErrors.amount = translations?.amountExceedsBalance || 'Payment amount exceeds current balance';
        }

        if ((paymentMethod === 'bank_transfer' || paymentMethod === 'digital_wallet') && !bankName) {
            newErrors.bankName = translations?.bankNameRequired || 'Bank/Wallet name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (saving) return;
        setSaving(true);
        
        try {
            const username = user?.username || localStorage.getItem('username');
            const idempotencyKey = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            const payload = {
                action: 'record_po_payment',
                customer_id: customer.customer_id,
                shop_id: shopId,
                amount: parseFloat(amount),
                payment_method: paymentMethod,
                payment_date: paymentDate,
                username: username,
                notes: notes,
                reference_no: referenceNo,
                bank_name: bankName,
                account_number: accountNumber,
                transaction_id: transactionId,
                idempotency_key: idempotencyKey
            };

            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(payload)
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                if (showToast) showToast('success', data.message || translations?.paymentSuccess || 'Payment recorded successfully!');
                await onSave(data.data);
                onClose();
            } else {
                if (showToast) showToast('error', data.message || translations?.paymentFailed || 'Payment failed');
            }
        } catch (error) {
            console.error('Payment error:', error);
            if (showToast) showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };
    
    const isRTL = (localStorage.getItem('ourzest_language') || 'en') === 'ur' || (localStorage.getItem('ourzest_language') || 'en') === 'ar';
    
    const paymentMethods = [
        { value: 'cash', label: translations?.cash || 'Cash', icon: faMoneyBill },
        { value: 'bank_transfer', label: translations?.bankTransfer || 'Bank Transfer', icon: faLandmark },
        { value: 'digital_wallet', label: translations?.digitalWallet || 'Digital Wallet', icon: faWallet }
    ];

    if (!show) return null;

    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-payment-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faMoneyBillWave} /> {translations?.receivePayment || 'Receive Payment'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    {customer && (
                        <div className="ca-customer-info-summary">
                            <div className="ca-customer-name-large">{customer.customer_name}</div>
                            {customer.phone && (
                                <div className="ca-customer-phone-large">
                                    <FontAwesomeIcon icon={faPhone} /> {customer.phone}
                                </div>
                            )}
                            <div className="ca-current-balance">
                                <span>{translations?.currentBalance || 'Balance Due'}:</span>
                                <span className={parseFloat(customer.current_balance) > 0 ? 'positive' : ''}>
                                    Rs. {formatCurrency(customer.current_balance || 0)}
                                </span>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faCreditCard} /> {translations?.paymentMethod || 'Payment Method'}</h3>
                            <div className="ca-payment-methods-grid">
                                {paymentMethods.map(method => (
                                    <button 
                                        key={method.value} 
                                        type="button" 
                                        className={`ca-payment-method-btn ${paymentMethod === method.value ? 'active' : ''}`} 
                                        onClick={() => setPaymentMethod(method.value)} 
                                        disabled={saving}
                                    >
                                        <FontAwesomeIcon icon={method.icon} />
                                        <span>{method.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="ca-form-section">
                            <h3 className="ca-section-title"><FontAwesomeIcon icon={faFileInvoice} /> {translations?.paymentDetails || 'Payment Details'}</h3>
                            <div className="ca-form-grid">
                                <div className={`ca-form-group ${errors.amount ? 'has-error' : ''}`}>
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="ca-input-icon" />
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.amount || 'Amount'} <span className="ca-required">*</span></label>
                                    </div>
                                    {errors.amount && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}</div>}
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="ca-input-icon" />
                                        <input
                                            type="date"
                                            value={paymentDate}
                                            onChange={(e) => setPaymentDate(e.target.value)}
                                            disabled={saving}
                                            dir="ltr"
                                        />
                                        <label className="ca-input-label">{translations?.paymentDate || 'Payment Date'}</label>
                                    </div>
                                </div>

                                <div className="ca-form-group">
                                    <div className="ca-input-container">
                                        <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                        <input
                                            type="text"
                                            value={referenceNo}
                                            onChange={(e) => setReferenceNo(e.target.value)}
                                            placeholder=" "
                                            disabled={saving}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        <label className="ca-input-label">{translations?.referenceNumber || 'Reference Number'}</label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {(paymentMethod === 'bank_transfer' || paymentMethod === 'digital_wallet') && (
                            <div className="ca-form-section">
                                <h3 className="ca-section-title"><FontAwesomeIcon icon={faUniversity} /> {translations?.bankDetails || 'Bank/Wallet Details'}</h3>
                                <div className="ca-form-grid">
                                    <div className={`ca-form-group ${errors.bankName ? 'has-error' : ''}`}>
                                        <div className="ca-input-container">
                                            <FontAwesomeIcon icon={faLandmark} className="ca-input-icon" />
                                            <input
                                                type="text"
                                                value={bankName}
                                                onChange={(e) => setBankName(e.target.value)}
                                                placeholder=" "
                                                disabled={saving}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                            />
                                            <label className="ca-input-label">{translations?.bankName || 'Bank/Wallet Name'} <span className="ca-required">*</span></label>
                                        </div>
                                        {errors.bankName && <div className="ca-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {errors.bankName}</div>}
                                    </div>

                                    <div className="ca-form-group">
                                        <div className="ca-input-container">
                                            <FontAwesomeIcon icon={faIdCard} className="ca-input-icon" />
                                            <input
                                                type="text"
                                                value={accountNumber}
                                                onChange={(e) => setAccountNumber(e.target.value)}
                                                placeholder=" "
                                                disabled={saving}
                                                dir="ltr"
                                            />
                                            <label className="ca-input-label">{translations?.accountNumber || 'Account Number'}</label>
                                        </div>
                                    </div>

                                    <div className="ca-form-group">
                                        <div className="ca-input-container">
                                            <FontAwesomeIcon icon={faBarcode} className="ca-input-icon" />
                                            <input
                                                type="text"
                                                value={transactionId}
                                                onChange={(e) => setTransactionId(e.target.value)}
                                                placeholder=" "
                                                disabled={saving}
                                                dir="ltr"
                                            />
                                            <label className="ca-input-label">{translations?.transactionId || 'Transaction ID'}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="ca-form-section">
                            <div className="ca-form-group ca-full-width">
                                <div className="ca-input-container ca-textarea-container">
                                    <FontAwesomeIcon icon={faReceipt} className="ca-input-icon" />
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder=" "
                                        disabled={saving}
                                        rows="3"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    ></textarea>
                                    <label className="ca-input-label">{translations?.notes || 'Notes'}</label>
                                </div>
                            </div>
                        </div>

                        <div className="ca-modal-footer">
                            <button type="button" className="ca-btn-secondary" onClick={onClose}>
                                {translations?.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ca-btn-primary" disabled={saving}>
                                {saving ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> {translations?.saving || 'Saving...'}</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> {translations?.savePayment || 'Save Payment'}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Payment Receipt Modal
// ==============================================
const CaPaymentReceiptModal = ({ show, payment, onClose, translations }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN');
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    
    if (!show || !payment) return null;
    
    return (
        <div className="ca-modal-overlay" onClick={onClose}>
            <div className="ca-modal-content ca-receipt-modal" onClick={e => e.stopPropagation()}>
                <div className="ca-modal-header">
                    <h3><FontAwesomeIcon icon={faReceipt} /> {translations?.paymentReceipt || 'Payment Receipt'}</h3>
                    <button className="ca-modal-close" onClick={onClose}>×</button>
                </div>
                <div className="ca-modal-body">
                    <div className="ca-receipt-preview">
                        <div className="ca-text-center"><strong>{translations?.paymentReceipt || 'PAYMENT RECEIPT'}</strong></div>
                        <hr />
                        <div><strong>{translations?.receiptNo || 'Receipt No'}:</strong> {payment.receipt_number}</div>
                        <div><strong>{translations?.date || 'Date'}:</strong> {formatDate(payment.payment_date)}</div>
                        <div><strong>{translations?.customerName || 'Customer'}:</strong> {payment.customer_name}</div>
                        <div><strong>{translations?.amount || 'Amount'}:</strong> Rs. {formatCurrency(payment.amount)}</div>
                        <div><strong>{translations?.paymentMethod || 'Method'}:</strong> {payment.payment_method?.replace('_', ' ').toUpperCase()}</div>
                        {payment.reference_no && <div><strong>{translations?.referenceNumber || 'Reference'}:</strong> {payment.reference_no}</div>}
                        {payment.bank_name && <div><strong>{translations?.bankName || 'Bank/Wallet'}:</strong> {payment.bank_name}</div>}
                        {payment.account_number && <div><strong>{translations?.accountNumber || 'Account No'}:</strong> {payment.account_number}</div>}
                        {payment.transaction_id && <div><strong>{translations?.transactionId || 'Transaction ID'}:</strong> {payment.transaction_id}</div>}
                        <div><strong>{translations?.balanceAfter || 'Balance After'}:</strong> Rs. {formatCurrency(payment.balance_after)}</div>
                        <hr />
                        <div className="ca-text-center">{translations?.thankYou || 'Thank you for your payment!'}</div>
                    </div>
                </div>
                <div className="ca-modal-footer">
                    <button className="ca-btn-secondary" onClick={onClose}>{translations?.close || 'Close'}</button>
                    <button className="ca-btn-primary" onClick={() => window.print()}><FontAwesomeIcon icon={faPrint} /> {translations?.print || 'Print'}</button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Filter Bar Component
// ==============================================
const TransactionFilterBar = ({ filters, onFilterChange, onReset, translations, showFilterStats, totalFiltered }) => {
    const [showFilters, setShowFilters] = useState(true);

    const transactionTypes = [
        { value: 'all', label: 'All Types', icon: faList },
        { value: 'sale', label: 'Sale', icon: faShoppingCart },
        { value: 'payment', label: 'Payment', icon: faMoneyBill },
        { value: 'manual_balance', label: 'Manual Balance', icon: faPlusCircle },
        { value: 'refund', label: 'Refund', icon: faMinusCircle }
    ];

    const sources = [
        { value: 'all', label: 'All Sources' },
        { value: 'transaction', label: 'Sales' },
        { value: 'payment', label: 'Payments' },
        { value: 'manual_balance', label: 'Manual' }
    ];

    return (
        <div className="ca-filter-bar">
            <div className="ca-filter-header">
                <button 
                    className="ca-filter-toggle" 
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>{translations?.filters || 'Filters'}</span>
                    <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
                </button>
                {showFilterStats && totalFiltered > 0 && (
                    <div className="ca-filter-stats">
                        <span className="ca-filter-count">{totalFiltered}</span>
                        <span className="ca-filter-label">transactions shown</span>
                    </div>
                )}
                <button className="ca-reset-filters" onClick={onReset}>
                    <FontAwesomeIcon icon={faRedoAlt} />
                    <span>{translations?.resetFilters || 'Reset'}</span>
                </button>
            </div>

            {showFilters && (
                <div className="ca-filter-body">
                    <div className="ca-filter-group">
                        <label className="ca-filter-label">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <span>Date Range</span>
                        </label>
                        <div className="ca-date-range">
                            <div className="ca-date-input">
                                <span>From</span>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                                />
                            </div>
                            <div className="ca-date-input">
                                <span>To</span>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => onFilterChange('dateTo', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ca-filter-group">
                        <label className="ca-filter-label">
                            <FontAwesomeIcon icon={faTag} />
                            <span>Transaction Type</span>
                        </label>
                        <div className="ca-filter-buttons">
                            {transactionTypes.map(type => (
                                <button
                                    key={type.value}
                                    className={`ca-filter-btn ${filters.transactionType === type.value ? 'active' : ''}`}
                                    onClick={() => onFilterChange('transactionType', type.value)}
                                >
                                    <FontAwesomeIcon icon={type.icon} />
                                    <span>{type.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="ca-filter-group">
                        <label className="ca-filter-label">
                            <FontAwesomeIcon icon={faBox} />
                            <span>Source</span>
                        </label>
                        <div className="ca-filter-buttons">
                            {sources.map(source => (
                                <button
                                    key={source.value}
                                    className={`ca-filter-btn ${filters.source === source.value ? 'active' : ''}`}
                                    onClick={() => onFilterChange('source', source.value)}
                                >
                                    <span>{source.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="ca-filter-group">
                        <label className="ca-filter-label">
                            <FontAwesomeIcon icon={faRupeeSign} />
                            <span>Amount Range (Rs.)</span>
                        </label>
                        <div className="ca-amount-range">
                            <input
                                type="number"
                                placeholder="Min Amount"
                                value={filters.amountMin}
                                onChange={(e) => onFilterChange('amountMin', e.target.value)}
                                className="ca-amount-input"
                            />
                            <span className="ca-amount-sep">-</span>
                            <input
                                type="number"
                                placeholder="Max Amount"
                                value={filters.amountMax}
                                onChange={(e) => onFilterChange('amountMax', e.target.value)}
                                className="ca-amount-input"
                            />
                        </div>
                    </div>

                    <div className="ca-filter-group">
                        <label className="ca-filter-label">
                            <FontAwesomeIcon icon={faSearch} />
                            <span>Reference No / Description</span>
                        </label>
                        <input
                            type="text"
                            placeholder="Search by reference number or description..."
                            value={filters.searchText}
                            onChange={(e) => onFilterChange('searchText', e.target.value)}
                            className="ca-filter-search"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ==============================================
// Main PO Customer Account Component
// ==============================================
const CustomerAccount = () => {
    const { user, currentShop } = useAuth();
    
    const [shopId, setShopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });
    
    const [allCustomers, setAllCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [accountData, setAccountData] = useState({
        current_balance: 0,
        transactions: [],
        payments: []
    });
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('transactions');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showManualBalanceModal, setShowManualBalanceModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        transactionType: 'all',
        source: 'all',
        amountMin: '',
        amountMax: '',
        searchText: ''
    });

    const [sortConfig, setSortConfig] = useState({
        key: 'transaction_date',
        direction: 'desc'
    });

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    const defaultTexts = {
        customerAccount: 'Customer Account',
        accountDetails: 'View and manage customer accounts, balances, and payment history',
        currentBalance: 'Balance Due',
        totalPurchases: 'Total Purchases',
        totalPayments: 'Total Payments',
        transactionHistory: 'All Transactions',
        payments: 'Payments',
        manualBalance: 'Add Manual Balance',
        manualBalanceSuccess: 'Manual balance added successfully',
        manualBalanceFailed: 'Failed to add manual balance',
        balanceDetails: 'Balance Details',
        balanceDate: 'Balance Date',
        balanceDateRequired: 'Balance date is required',
        balanceSummary: 'Balance Summary',
        amountToAdd: 'Amount to Add',
        newBalance: 'New Balance After Addition',
        adding: 'Adding...',
        addBalance: 'Add Balance',
        manualBalanceHelp: 'Enter the previous due amount to add to customer\'s account',
        description: 'Description',
        recordPayment: 'Record Payment',
        selectCustomer: 'Select Customer',
        searchCustomer: 'Search customers by name or phone...',
        noCustomersFound: 'No customers found',
        noTransactions: 'No transactions found',
        noPayments: 'No payments found',
        paymentMethod: 'Payment Method',
        paymentDate: 'Payment Date',
        referenceNumber: 'Reference Number',
        notes: 'Notes',
        cash: 'Cash',
        bankTransfer: 'Bank Transfer',
        digitalWallet: 'Digital Wallet',
        receiptNo: 'Receipt No',
        amount: 'Amount',
        date: 'Date',
        customerInfo: 'Customer Information',
        phone: 'Phone',
        email: 'Email',
        address: 'Address',
        balanceAfter: 'Balance After',
        bankDetails: 'Bank/Wallet Details',
        bankName: 'Bank Name',
        accountNumber: 'Account Number',
        transactionId: 'Transaction ID',
        savePayment: 'Save Payment',
        cancel: 'Cancel',
        close: 'Close',
        print: 'Print',
        paymentReceipt: 'Payment Receipt',
        thankYou: 'Thank you for your payment!',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        loading: 'Loading...',
        saving: 'Saving...',
        paymentSuccess: 'Payment recorded successfully!',
        paymentFailed: 'Failed to record payment',
        invalidAmount: 'Please enter a valid amount',
        amountExceedsBalance: 'Payment amount exceeds current balance',
        bankNameRequired: 'Bank/Wallet name is required',
        networkError: 'Network error occurred',
        receivePayment: 'Receive Payment',
        type: 'Type',
        sale: 'Sale',
        adjustment: 'Adjustment',
        refund: 'Refund',
        customers: 'Customers',
        noCustomers: 'No customers available',
        quickPayment: 'Quick Payment',
        validAmountRequired: 'Please enter a valid amount',
        filters: 'Filters',
        resetFilters: 'Reset'
    };

    useEffect(() => {
        setTranslations(defaultTexts);
    }, []);

    const applyFilters = useCallback(() => {
        if (!accountData.transactions) return;
        
        let filtered = [...accountData.transactions];
        
        if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(t => new Date(t.transaction_date) >= fromDate);
        }
        if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(t => new Date(t.transaction_date) <= toDate);
        }
        
        if (filters.transactionType !== 'all') {
            filtered = filtered.filter(t => t.transaction_type === filters.transactionType);
        }
        
        if (filters.source !== 'all') {
            filtered = filtered.filter(t => t.source === filters.source);
        }
        
        if (filters.amountMin !== '') {
            const minAmount = parseFloat(filters.amountMin);
            if (!isNaN(minAmount)) {
                filtered = filtered.filter(t => Math.abs(t.amount) >= minAmount);
            }
        }
        if (filters.amountMax !== '') {
            const maxAmount = parseFloat(filters.amountMax);
            if (!isNaN(maxAmount)) {
                filtered = filtered.filter(t => Math.abs(t.amount) <= maxAmount);
            }
        }
        
        if (filters.searchText.trim() !== '') {
            const searchLower = filters.searchText.toLowerCase();
            filtered = filtered.filter(t => 
                (t.reference_number && t.reference_number.toLowerCase().includes(searchLower)) ||
                (t.description && t.description.toLowerCase().includes(searchLower)) ||
                (t.receipt_number && t.receipt_number.toLowerCase().includes(searchLower))
            );
        }
        
        setFilteredTransactions(filtered);
    }, [accountData.transactions, filters]);

    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const resetFilters = () => {
        setFilters({
            dateFrom: '',
            dateTo: '',
            transactionType: 'all',
            source: 'all',
            amountMin: '',
            amountMax: '',
            searchText: ''
        });
    };

    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
        }
        
        const themeColor = localStorage.getItem('ourzest_theme') || '#6366f1';
        const root = document.documentElement;
        const hex = themeColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        const adjust = (amount) => {
            let newR = Math.max(0, Math.min(255, r + amount));
            let newG = Math.max(0, Math.min(255, g + amount));
            let newB = Math.max(0, Math.min(255, b + amount));
            return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
        };

        root.style.setProperty('--ca-primary', themeColor);
        root.style.setProperty('--ca-primary-dark', adjust(-40));
        root.style.setProperty('--ca-primary-light', adjust(40));
        root.style.setProperty('--ca-primary-ultralight', adjust(20));
        root.style.setProperty('--ca-primary-gradient', `linear-gradient(135deg, ${themeColor}, ${adjust(-20)})`);
        root.style.setProperty('--ca-primary-shadow', `${themeColor}20`);
    }, [currentLanguage]);

    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
            }
        };

        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    const loadAllCustomers = useCallback(async () => {
        if (!user?.username || !currentShop) return;
        setLoadingCustomers(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_po_all_customers_account',
                    shop_id: currentShop.shop_id,
                    username: user.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setAllCustomers(data.data || []);
            } else {
                setAllCustomers([]);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setAllCustomers([]);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user?.username, currentShop]);

    const loadCustomerAccount = async (customerId) => {
        if (!currentShop) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'get_po_customer_account', 
                    customer_id: customerId, 
                    shop_id: currentShop.shop_id,
                    username: user?.username 
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const allTransactions = [
                    ...(data.data.transactions || []).map(t => ({ ...t, source: 'transaction' })),
                    ...(data.data.payments || []).map(p => ({ 
                        ...p, 
                        source: 'payment',
                        transaction_type: 'payment',
                        transaction_date: p.payment_date,
                        description: `Payment received via ${p.payment_method}`,
                        reference_number: p.receipt_number
                    })),
                    ...(data.data.manual_balances || []).map(m => ({ 
                        ...m, 
                        source: 'manual_balance',
                        transaction_type: 'manual_balance',
                        description: m.description || 'Manual balance adjustment',
                        reference_number: m.reference_number || m.reference_no
                    }))
                ];
                
                allTransactions.sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));
                
                setAccountData({
                    current_balance: data.data.current_balance,
                    transactions: allTransactions,
                    payments: data.data.payments || []
                });
                setSelectedCustomer(prev => prev ? { ...prev, current_balance: data.data.current_balance } : prev);
                resetFilters();
            } else {
                showToast('error', data.message || 'Failed to load account');
            }
        } catch (error) {
            console.error('Error loading account:', error);
            showToast('error', 'Network error');
        } finally {
            setLoading(false);
        }
    };

    const searchCustomers = useCallback(async (term) => {
        if (!user?.username || !currentShop) return;
        if (!term || term.length < 2) {
            loadAllCustomers();
            return;
        }
        setLoadingCustomers(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/pocustaccount.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'search_po_customers_account',
                    search_term: term,
                    shop_id: currentShop.shop_id,
                    username: user.username
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setAllCustomers(data.data || []);
            }
        } catch (error) {
            console.error('Error searching:', error);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user?.username, currentShop, loadAllCustomers]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (searchTerm.length >= 2) {
                searchCustomers(searchTerm);
            } else if (searchTerm.length === 0) {
                loadAllCustomers();
            }
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm, searchCustomers, loadAllCustomers]);

    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        loadCustomerAccount(customer.customer_id);
    };

    const handlePaymentSubmit = async (paymentData) => {
        if (!selectedCustomer || !currentShop) return;
        
        try {
            await loadCustomerAccount(selectedCustomer.customer_id);
            await loadAllCustomers();
            
            setSelectedPayment({
                ...paymentData,
                customer_name: selectedCustomer.customer_name
            });
            setShowReceiptModal(true);
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    const handleManualBalanceSubmit = async (balanceData) => {
        if (!selectedCustomer || !currentShop) return;
        await loadCustomerAccount(selectedCustomer.customer_id);
        await loadAllCustomers();
    };

    const refreshAfterManualBalance = async () => {
        if (selectedCustomer) {
            await loadCustomerAccount(selectedCustomer.customer_id);
            await loadAllCustomers();
        }
    };

    const refreshAfterEditDelete = async () => {
        if (selectedCustomer) {
            await loadCustomerAccount(selectedCustomer.customer_id);
            await loadAllCustomers();
        }
    };

    const formatCurrency = (amount) => {
        return `Rs. ${parseFloat(amount || 0).toFixed(2)}`;
    };

    const formatDate = (dateString) => {
        return dateString ? new Date(dateString).toLocaleDateString('en-IN') : '';
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };
    
    const getSortedData = (data) => {
        if (!data || !data.length) return [];
        
        const sorted = [...data];
        sorted.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            
            if (sortConfig.key === 'transaction_date' || sortConfig.key === 'payment_date' || sortConfig.key === 'transfer_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'amount') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
            }
            
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const filteredCustomers = allCustomers.filter(customer => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (customer.customer_name?.toLowerCase().includes(searchLower)) ||
               (customer.phone?.includes(searchTerm));
    });

    useEffect(() => {
        if (currentShop && user?.username) {
            loadAllCustomers();
        }
    }, [currentShop, user?.username, loadAllCustomers]);

    const sortedTransactions = getSortedData(filteredTransactions);
    const sortedPayments = getSortedData(accountData.payments || []);

    return (
        <div className={`ca-container ${isRTL ? 'ca-rtl' : ''}`}>
            <CaToastNotification toast={toast} setToast={setToast} translations={translations} />
            
            <POEnhancedPaymentModal 
                show={showPaymentModal} 
                customer={selectedCustomer} 
                onClose={() => setShowPaymentModal(false)} 
                onSave={handlePaymentSubmit} 
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaManualBalanceModal 
                show={showManualBalanceModal} 
                customer={selectedCustomer} 
                onClose={() => setShowManualBalanceModal(false)} 
                onSave={handleManualBalanceSubmit} 
                onRefresh={refreshAfterManualBalance}
                translations={translations}
                showToast={showToast}
                currentShopId={shopId}
            />

            <CaPaymentReceiptModal 
                show={showReceiptModal} 
                payment={selectedPayment} 
                onClose={() => setShowReceiptModal(false)} 
                translations={translations}
            />

            <EditTransactionModal
                show={showEditModal}
                transaction={selectedTransaction}
                customer={selectedCustomer}
                onClose={() => setShowEditModal(false)}
                onSave={refreshAfterEditDelete}
                showToast={showToast}
                currentShopId={shopId}
            />

            <DeleteTransactionModal
                show={showDeleteModal}
                transaction={selectedTransaction}
                customer={selectedCustomer}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={refreshAfterEditDelete}
                showToast={showToast}
                currentShopId={shopId}
            />

            <div className="ca-header">
                <div className="ca-header-content">
                    <div className="ca-header-icon"><FontAwesomeIcon icon={faUsers} /></div>
                    <div className="ca-header-text">
                        <h1>{translations.customerAccount}</h1>
                        <p>{translations.accountDetails}</p>
                        {currentShop && (
                            <div className="ca-shop-badge"><FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="ca-main-layout">
                <div className={`ca-sidebar ${sidebarCollapsed ? 'ca-collapsed' : ''}`}>
                    <div className="ca-sidebar-header">
                        <button className="ca-sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="ca-sidebar-title"><FontAwesomeIcon icon={faUsers} /><span>{translations.customers}</span></div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="ca-sidebar-search">
                                <div className="ca-search-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="ca-search-icon" />
                                    <input 
                                        type="text" 
                                        placeholder={translations.searchCustomer} 
                                        value={searchTerm} 
                                        onChange={(e) => setSearchTerm(e.target.value)} 
                                    />
                                    {loadingCustomers && <FontAwesomeIcon icon={faSpinner} spin className="ca-search-spinner" />}
                                </div>
                            </div>
                            <div className="ca-customer-list">
                                {loadingCustomers ? (
                                    <div className="ca-loading-customers"><FontAwesomeIcon icon={faSpinner} spin /><span>{translations.loading}</span></div>
                                ) : filteredCustomers.length === 0 ? (
                                    <div className="ca-no-customers"><FontAwesomeIcon icon={faUsers} /><span>{translations.noCustomers}</span></div>
                                ) : (
                                    filteredCustomers.map(customer => (
                                        <div 
                                            key={customer.customer_id} 
                                            className={`ca-customer-item ${selectedCustomer?.customer_id === customer.customer_id ? 'ca-active' : ''}`} 
                                            onClick={() => selectCustomer(customer)}
                                        >
                                            <div className="ca-customer-content">
                                                <div className="ca-customer-name">
                                                    <FontAwesomeIcon icon={faUser} className="ca-item-icon" />
                                                    <span className="ca-customer-name-text">{customer.customer_name}</span>
                                                </div>
                                                {customer.phone && (
                                                    <div className="ca-customer-phone">
                                                        <FontAwesomeIcon icon={faPhone} size="xs" /> {customer.phone}
                                                    </div>
                                                )}
                                                <div className={`ca-customer-balance ${parseFloat(customer.current_balance) > 0 ? 'ca-positive' : ''}`}>
                                                    {formatCurrency(customer.current_balance || 0)}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="ca-main-content">
                    {selectedCustomer ? (
                        <div className="ca-account-content">
                            <div className="ca-summary-cards">
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faUser} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.customerInfo}</div>
                                        <div className="ca-card-value">{selectedCustomer.customer_name}</div>
                                        {selectedCustomer.phone && (
                                            <div className="ca-card-subvalue"><FontAwesomeIcon icon={faPhone} size="xs" /> {selectedCustomer.phone}</div>
                                        )}
                                    </div>
                                </div>
                                
                                <div className={`ca-summary-card ca-balance-card ${parseFloat(accountData.current_balance) > 0 ? 'ca-balance-positive' : ''}`}>
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.currentBalance}</div>
                                        <div className="ca-card-value">{formatCurrency(accountData.current_balance)}</div>
                                    </div>
                                </div>
                                
                                <div className="ca-summary-card">
                                    <div className="ca-card-icon"><FontAwesomeIcon icon={faMoneyBill} /></div>
                                    <div className="ca-card-content">
                                        <div className="ca-card-label">{translations.totalPayments}</div>
                                        <div className="ca-card-value">{formatCurrency(accountData.account?.total_payments || 0)}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="ca-action-buttons">
                                <button className="ca-btn-secondary" onClick={() => setShowPaymentModal(true)}>
                                    <FontAwesomeIcon icon={faMoneyBill} /> {translations.recordPayment || 'Record Payment'}
                                </button>
                                <button className="ca-btn-manual" onClick={() => setShowManualBalanceModal(true)}>
                                    <FontAwesomeIcon icon={faPlusCircle} /> {translations.manualBalance || 'Add Manual Balance'}
                                </button>
                            </div>

                            <div className="ca-tabs">
                                <button className={`ca-tab-btn ${activeTab === 'transactions' ? 'ca-active' : ''}`} onClick={() => setActiveTab('transactions')}>
                                    <FontAwesomeIcon icon={faHistory} /> All Transactions
                                </button>
                                <button className={`ca-tab-btn ${activeTab === 'payments' ? 'ca-active' : ''}`} onClick={() => setActiveTab('payments')}>
                                    <FontAwesomeIcon icon={faMoneyBill} /> {translations.payments}
                                </button>
                            </div>

                            <div className="ca-tab-content">
                                {activeTab === 'transactions' && (
                                    <>
                                        <TransactionFilterBar
                                            filters={filters}
                                            onFilterChange={updateFilter}
                                            onReset={resetFilters}
                                            translations={translations}
                                            showFilterStats={true}
                                            totalFiltered={sortedTransactions.length}
                                        />
                                        
                                        <div className="ca-table-container">
                                            <table className="ca-table">
                                                <thead>
                                                    <tr>
                                                        <CaSortableHeader column="transaction_date" label="Date" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <CaSortableHeader column="transaction_type" label="Type" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <CaSortableHeader column="description" label="Description" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <CaSortableHeader column="amount" label="Amount" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <CaSortableHeader column="balance_after" label="Balance After" sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                        <th>Reference No</th>
                                                        <th>Source</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sortedTransactions.map((t, i) => (
                                                        <tr key={t.id || i}>
                                                            {/* <td>{formatDate(t.transaction_date)}</ */}
                                                                                                                        <td>{formatDate(t.transaction_date)}</td>
                                                            <td>
                                                                <span className={`ca-transaction-type ca-${t.transaction_type}`}>
                                                                    {t.transaction_type === 'sale' ? 'Sale' : 
                                                                     t.transaction_type === 'payment' ? 'Payment' :
                                                                     t.transaction_type === 'manual_balance' ? 'Manual Balance' : 
                                                                     t.transaction_type === 'adjustment' ? 'Adjustment' : 'Refund'}
                                                                </span>
                                                            </td>
                                                            <td>{t.description || '-'}</td>
                                                            <td className={`ca-text-right ${t.transaction_type === 'sale' || t.transaction_type === 'manual_balance' ? 'ca-debit' : 'ca-credit'}`}>
                                                                {t.transaction_type === 'sale' || t.transaction_type === 'manual_balance' ? `+ ${formatCurrency(t.amount)}` : `- ${formatCurrency(Math.abs(t.amount))}`}
                                                            </td>
                                                            <td className="ca-text-right">{formatCurrency(t.balance_after)}</td>
                                                            <td>{t.reference_number || '-'}</td>
                                                            <td>
                                                                <span className={`ca-source-badge ca-source-${t.source}`}>
                                                                    {t.source === 'transaction' ? 'Sale' : t.source === 'payment' ? 'Payment' : 'Manual'}
                                                                </span>
                                                            </td>
                                                            <td className="ca-action-buttons-cell">
                                                                <button 
                                                                    className="ca-btn-icon ca-edit-btn" 
                                                                    onClick={() => {
                                                                        setSelectedTransaction(t);
                                                                        setShowEditModal(true);
                                                                    }}
                                                                    title="Edit Transaction"
                                                                >
                                                                    <FontAwesomeIcon icon={faEdit} />
                                                                </button>
                                                                <button 
                                                                    className="ca-btn-icon ca-delete-btn" 
                                                                    onClick={() => {
                                                                        setSelectedTransaction(t);
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                    title="Delete Transaction"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {sortedTransactions.length === 0 && (
                                                        <tr>
                                                            <td colSpan="8" className="ca-text-center ca-no-data">
                                                                {filters.dateFrom || filters.dateTo || filters.transactionType !== 'all' || filters.source !== 'all' || filters.amountMin || filters.amountMax || filters.searchText 
                                                                    ? 'No transactions match the selected filters'
                                                                    : translations.noTransactions}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}

                                {activeTab === 'payments' && (
                                    <div className="ca-table-container">
                                        <table className="ca-table">
                                            <thead>
                                                <tr>
                                                    <CaSortableHeader column="receipt_number" label={translations.receiptNo} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <CaSortableHeader column="payment_date" label={translations.date} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <CaSortableHeader column="payment_method" label={translations.paymentMethod} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <CaSortableHeader column="amount" label={translations.amount} sortConfig={sortConfig} onSort={handleSort} translations={translations} />
                                                    <th>{translations.bankName || 'Bank/Wallet'}</th>
                                                    <th>{translations.notes}</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedPayments.map(payment => (
                                                    <tr key={payment.id}>
                                                        <td>{payment.receipt_number}</td>
                                                        <td>{formatDate(payment.payment_date)}</td>
                                                        <td><span className="ca-payment-method">{payment.payment_method?.replace('_', ' ')}</span></td>
                                                        <td className="ca-text-right ca-positive">{formatCurrency(payment.amount)}</td>
                                                        <td>{payment.bank_name || '-'}</td>
                                                        <td>{payment.notes || '-'}</td>
                                                        <td>
                                                            <button className="ca-btn-icon" onClick={() => { 
                                                                setSelectedPayment({
                                                                    receipt_number: payment.receipt_number,
                                                                    payment_date: payment.payment_date,
                                                                    payment_method: payment.payment_method,
                                                                    amount: payment.amount,
                                                                    balance_after: payment.balance_after,
                                                                    customer_name: selectedCustomer.customer_name,
                                                                    reference_no: payment.reference_number,
                                                                    bank_name: payment.bank_name,
                                                                    account_number: payment.account_number,
                                                                    transaction_id: payment.transaction_id
                                                                }); 
                                                                setShowReceiptModal(true); 
                                                            }}>
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {sortedPayments.length === 0 && (
                                                    <tr>
                                                        <td colSpan="7" className="ca-text-center ca-no-data">
                                                            {translations.noPayments}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="ca-no-selected">
                            <FontAwesomeIcon icon={faUsers} className="ca-empty-icon" />
                            <h3>{translations.selectCustomer}</h3>
                            <p>Select a customer from the list to view their account details</p>
                        </div>
                    )}
                </div>
            </div>

            {loading && (
                <div className="ca-loading-overlay">
                    <div className="ca-loading-content">
                        <div className="ca-loading-spinner"></div>
                        <p>{translations.loading}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerAccount;